import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef
} from 'react';
import * as Sentry from '@sentry/react';
import {
  GrowthTrackerEstimationMethods,
  idToNameTestItem,
  Test
} from 'constants.js';
import { useHistory } from 'react-router-dom';
import { differenceInWeeks, format } from 'date-fns';
import { QUERY_GET_GROWTH_DATA_OF_GROUP } from 'services/aws/growthtracker-query';
import { useLazyQuery } from '@apollo/client';
import { createColumnHelper } from '@tanstack/react-table';
import { QUERY_GET_TESTDATA_HISTORY } from 'services/aws/session-query';
import { useIntl } from 'react-intl';
import messages from 'messages';
import { formatValue } from 'utils/results';

export const GrowthPanelContext = createContext({});

const columnHelper = createColumnHelper();

const formatCell = data => {
  return data ? `${data.value} ${data.unit}` : '-';
};

export const sortByValue = (rowA, rowB, _columnId) => {
  const valueA = rowA.getValue(_columnId).value;
  const valueB = rowB.getValue(_columnId).value;

  if (valueA === undefined && valueB === undefined) {
    return 0;
  }
  if (valueA === undefined) {
    return -1;
  }
  if (valueB === undefined) {
    return 1;
  }

  return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
};

export const sortByFirstValue = (rowA, rowB, _columnId) => {
  let valueA = rowA.original[_columnId].value;
  let valueB = rowB.original[_columnId].value;

  if (valueA === undefined && valueB === undefined) {
    return 0;
  }
  if (valueA === undefined) {
    return -1;
  }
  if (valueB === undefined) {
    return 1;
  }

  valueA = Number(valueA.split(' - ')[0]);
  valueB = Number(valueB.split(' - ')[0]);

  return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
};

export const sortByAlertLevel = (rowA, rowB, _columnId) => {
  let valueA = rowA.original[_columnId].alertLevel;
  let valueB = rowB.original[_columnId].alertLevel;

  if (
    (valueA === undefined || valueA === false) &&
    (valueB === undefined || valueB === false)
  ) {
    return 0;
  }
  if (valueA === undefined || valueA === false) {
    return -1;
  }
  if (valueB === undefined || valueB === false) {
    return 1;
  }

  valueA = Number(valueA);
  valueB = Number(valueB);

  return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
};

export const sortByArrayValue = (rowA, rowB, _columnId) => {
  let valueA = rowA.original[_columnId].value[1];
  let valueB = rowB.original[_columnId].value[1];

  if ((valueA === undefined || !valueA) && (valueB === undefined || !valueB)) {
    return 0;
  }
  if (valueA === undefined || !valueA) {
    return -1;
  }
  if (valueB === undefined || !valueB) {
    return 1;
  }

  valueA = Number(valueA);
  valueB = Number(valueB);

  return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
};

const GrowthTrackerColumns = (intl, method) => {
  const columns = [
    columnHelper.accessor('person', {
      header: () => '',
      accessorFn: row => `${row.person.firstname} ${row.person.lastname}`,
      size: 200,
      minSize: 150,
      sortingFn: 'alphanumeric',
      enableHiding: false
    }),
    columnHelper.accessor('preciseAge', {
      header: () => (
        <>
          {intl.formatMessage(messages.growthPredictionChronoAge, {
            isShort: true,
            br: () => <br />
          })}
        </>
      ),
      cell: props => formatCell(props),
      size: 96,
      sortingFn: 'sortByValue',
      sortUndefined: false
    }),
    columnHelper.accessor('bodyHeight', {
      header: () => (
        <>
          {intl.formatMessage(messages.growthPredictionBodyHeight, {
            isShort: true,
            br: () => <br />
          })}
        </>
      ),
      cell: props => formatCell(props),
      size: 96,
      sortingFn: 'sortByValue',
      sortUndefined: false
    }),
    columnHelper.accessor('bodyWeight', {
      id: 'bodyWeight',
      header: () => (
        <>
          {intl.formatMessage(messages.growthPredictionBodyWeight, {
            isShort: true,
            br: () => <br />
          })}
        </>
      ),
      cell: props => formatCell(props),
      size: 96,
      sortingFn: 'sortByValue',
      sortUndefined: false
    }),
    columnHelper.accessor('adultHeightPrediction', {
      id: 'adultHeightPrediction',
      header: () => (
        <>
          {intl.formatMessage(messages.growthPredictionAdultHPredict, {
            isShort: true,
            br: () => <br />
          })}
        </>
      ),
      cell: props => formatCell(props),
      size: 112,
      sortingFn: 'sortByValue',
      sortUndefined: false,
      meta: {
        longLabel: () => (
          <>
            {intl.formatMessage(messages.growthPredictionAdultHPredict, {
              isShort: false,
              br: () => <br />
            })}
          </>
        )
      }
    }),
    columnHelper.accessor('predictedAdultHeight', {
      id: 'predictedAdultHeight',
      header: () => (
        <>
          {intl.formatMessage(
            method === GrowthTrackerEstimationMethods.KHAMIS_ROCHE
              ? messages.growthPredictionPredictedAdultHeightKR
              : messages.growthPredictionPredictedAdultHeightSB,
            {
              isShort: true,
              br: () => <br />
            }
          )}
        </>
      ),
      cell: props => formatCell(props),
      size: 112,
      sortingFn: 'sortByValue',
      sortUndefined: false,
      meta: {
        longLabel: () => (
          <>
            {intl.formatMessage(
              method === GrowthTrackerEstimationMethods.KHAMIS_ROCHE
                ? messages.growthPredictionPredictedAdultHeightKR
                : messages.growthPredictionPredictedAdultHeightSB,
              {
                isShort: false
              }
            )}
          </>
        )
      }
    }),
    columnHelper.accessor('growthPhase', {
      id: 'growthPhase',
      header: () => (
        <>
          {intl.formatMessage(messages.growthPredictionMaturityStatus, {
            isShort: true,
            br: () => <br />
          })}
        </>
      ),
      cell: props => formatCell(props),
      size: 96,
      sortingFn: 'sortByAlertLevel',
      sortUndefined: false,
      meta: {
        longLabel: () => (
          <>
            {intl.formatMessage(messages.growthPredictionMaturityStatus, {
              isShort: false
            })}
          </>
        )
      }
    }),
    columnHelper.accessor('developmentZScore', {
      id: 'developmentZScore',
      header: () => (
        <>
          {intl.formatMessage(messages.growthPredictionMaturationTiming, {
            isShort: true,
            br: () => <br />
          })}
        </>
      ),
      cell: props => formatCell(props),
      size: 112,
      sortingFn: 'sortByArrayValue',
      sortUndefined: false,
      meta: {
        longLabel: () => (
          <>
            {intl.formatMessage(messages.growthPredictionMaturationTiming, {
              isShort: false
            })}
          </>
        )
      }
    }),
    columnHelper.accessor('heightVelocity', {
      id: 'heightVelocity',
      header: () => (
        <>
          {intl.formatMessage(messages.growthPredictionGrowthVelocity, {
            isShort: true,
            br: () => <br />
          })}
        </>
      ),
      cell: props => formatCell(props),
      size: 128,
      sortingFn: 'sortByValue',
      sortUndefined: false,
      meta: {
        longLabel: () => (
          <>
            {intl.formatMessage(messages.growthPredictionGrowthVelocity, {
              isShort: false
            })}
          </>
        )
      }
    }),
    columnHelper.accessor('weightVelocity', {
      id: 'weightVelocity',
      header: () => (
        <>
          {intl.formatMessage(messages.growthPredictionWeightVelocity, {
            br: () => <br />
          })}
        </>
      ),
      cell: props => formatCell(props),
      size: 128,
      sortingFn: 'sortByValue',
      sortUndefined: false
    }),
    columnHelper.accessor('bioAge', {
      id: 'bioAge',
      header: () => (
        <>
          {intl.formatMessage(
            method === GrowthTrackerEstimationMethods.KHAMIS_ROCHE
              ? messages.growthPredictionBioAge
              : messages.growthPredictionSkeletalAge,
            {
              br: () => <br />
            }
          )}
        </>
      ),
      cell: props => formatCell(props),
      size: 96,
      sortingFn: 'sortByValue',
      sortUndefined: false
    }),
    columnHelper.accessor('chronoVsBio', {
      id: 'chronoVsBio',
      header: () => (
        <>
          {intl.formatMessage(
            method === GrowthTrackerEstimationMethods.KHAMIS_ROCHE
              ? messages.growthPredictionChronoVsBioAge
              : messages.growthPredictionChronoVsSkeletalAge,
            {
              isShort: true,
              br: () => <br />
            }
          )}
        </>
      ),
      cell: props => formatCell(props),
      size: 96,
      sortingFn: 'sortByValue',
      sortUndefined: false,
      meta: {
        longLabel: () => (
          <>
            {intl.formatMessage(
              method === GrowthTrackerEstimationMethods.KHAMIS_ROCHE
                ? messages.growthPredictionChronoVsBioAge
                : messages.growthPredictionChronoVsSkeletalAge,
              {
                isShort: false
              }
            )}
          </>
        )
      }
    })
  ];

  if (method === GrowthTrackerEstimationMethods.KHAMIS_ROCHE) {
    columns.push(
      columnHelper.accessor('pah90Ci', {
        id: 'pah90Ci',
        header: () => (
          <>
            {intl.formatMessage(messages.growthPredictionPAH90CI, {
              isShort: true,
              br: () => <br />
            })}
          </>
        ),
        cell: props => formatCell(props),
        size: 112,
        sortingFn: 'sortByFirstValue',
        sortUndefined: false
      })
    );
  }

  return columns;
};

const getAlertOldData = data => {
  return (
    data.heightEvolutionData.length > 0 &&
    (differenceInWeeks(
      new Date(),
      new Date(
        data.heightEvolutionData[
          data.heightEvolutionData.length - 1
        ].testDateTime
      )
    ) > 10 ||
      (data.weightEvolutionData.length > 0 &&
        differenceInWeeks(
          new Date(),
          new Date(
            data.weightEvolutionData[
              data.weightEvolutionData.length - 1
            ].testDateTime
          )
        ) > 10))
  );
};

const getPAHLevel = growthPhase => {
  switch (true) {
    case growthPhase.toLowerCase().indexOf('pre') >= 0:
      return 3;

    case growthPhase.toLowerCase().indexOf('start') >= 0:
      return 1;

    case growthPhase.toLowerCase().indexOf('circa') >= 0:
      return 0;

    case growthPhase.toLowerCase().indexOf('post') >= 0:
      return 4;

    default:
      return 2;
  }
};

const getValue = testItemValues => {
  return testItemValues && Object.keys(testItemValues).length > 0
    ? testItemValues[0]
    : undefined;
};

const getRowData = (data, method) => {
  const lastHeightEvolution =
    data.heightEvolutionData.length > 0
      ? data.heightEvolutionData?.[data.heightEvolutionData.length - 1]
      : null;
  const lastWeightEvolution =
    data.weightEvolutionData.length > 0
      ? data.weightEvolutionData?.[data.weightEvolutionData.length - 1]
      : null;
  const getTestId = (khamis, sb) =>
    method === GrowthTrackerEstimationMethods.KHAMIS_ROCHE ? khamis : sb;

  const rowData = {
    preciseAge: {
      value: data.preciseAge ? data.preciseAge : false,
      unit: 'yearShort'
    },
    bodyHeight: {
      value: getValue(data.testItemValues[Test.BODY_HEIGHT]),
      unit: 'cm',
      testItemId: Test.BODY_HEIGHT
    },
    bodyWeight: {
      value: getValue(data.testItemValues[Test.BODY_WEIGHT]),
      unit: 'kg',
      testItemId: Test.BODY_WEIGHT
    },
    adultHeightPrediction: {
      value: getValue(
        data.testItemValues[
          getTestId(
            Test.ADULT_HEIGHT_PREDICTION_K_R,
            Test.ADULT_HEIGHT_PREDICTION_SB
          )
        ] //'5737716d-7eb3-4724-85d2-7baa4652ae93'
      ),
      unit: 'cm'
    },
    predictedAdultHeight: {
      value: getValue(
        data.testItemValues[
          getTestId(
            Test.PERCENT_ADULT_HEIGHT_PREDICTION,
            Test.PERCENT_ADULT_HEIGHT_PREDICTION_SB
          )
        ]
      ),
      unit: '%',
      alertLevel: !!getValue(
        data.testItemValues[
          getTestId(Test.GROWTH_PHASE, Test.MATURITY_STATUS_SB)
        ]
      )
        ? getPAHLevel(
            data.testItemValues[
              getTestId(Test.GROWTH_PHASE, Test.MATURITY_STATUS_SB)
            ][0]
          )
        : false,
      iconWarning: !!getValue(
        data.testItemValues[
          getTestId(Test.GROWTH_PHASE, Test.MATURITY_STATUS_SB)
        ]
      )
        ? getPAHLevel(
            data.testItemValues[
              getTestId(Test.GROWTH_PHASE, Test.MATURITY_STATUS_SB)
            ][0]
          ) <= 1
        : false
    },
    growthPhase: {
      value: getValue(
        data.testItemValues[
          getTestId(Test.GROWTH_PHASE, Test.MATURITY_STATUS_SB)
        ]
      ),
      unit: '',
      alertLevel: !!getValue(
        data.testItemValues[
          getTestId(Test.GROWTH_PHASE, Test.MATURITY_STATUS_SB)
        ]
      )
        ? getPAHLevel(
            data.testItemValues[
              getTestId(Test.GROWTH_PHASE, Test.MATURITY_STATUS_SB)
            ][0]
          )
        : false,
      iconWarning: !!getValue(
        data.testItemValues[
          getTestId(Test.GROWTH_PHASE, Test.MATURITY_STATUS_SB)
        ]
      )
        ? getPAHLevel(
            data.testItemValues[
              getTestId(Test.GROWTH_PHASE, Test.MATURITY_STATUS_SB)
            ][0]
          ) <= 1
        : false
    },
    developmentZScore: {
      value:
        method === GrowthTrackerEstimationMethods.KHAMIS_ROCHE
          ? [
              getValue(data.testItemValues[Test.MATURATION_TIMING]),
              !!getValue(data.testItemValues[Test.DEVELOPMENT_Z_SCORE])
                ? formatValue(
                    getValue(data.testItemValues[Test.DEVELOPMENT_Z_SCORE]),
                    2
                  )
                : false
            ]
          : [getValue(data.testItemValues[Test.MATURATION_TIMING_SB])],

      unit: ''
    },
    heightVelocity: {
      value: lastHeightEvolution?.growthYear,
      unit: 'cmYearShort',
      alert:
        lastHeightEvolution?.testDateTime &&
        differenceInWeeks(
          new Date(),
          new Date(lastHeightEvolution.testDateTime)
        ) > 10,
      alertLevel: lastHeightEvolution?.alertLevel,
      iconWarning: lastHeightEvolution?.alertLevel < 2,
      testItemId: 'heightEvolution'
    },
    weightVelocity: {
      value: lastWeightEvolution?.growthYear,
      unit: 'kgYearShort',
      alert:
        lastWeightEvolution?.testDateTime &&
        differenceInWeeks(
          new Date(),
          new Date(lastWeightEvolution.testDateTime)
        ) > 10,
      alertLevel: lastWeightEvolution?.alertLevel,
      iconWarning: lastWeightEvolution?.alertLevel < 2,
      testItemId: 'weightEvolution'
    },
    bioAge: {
      value: getValue(
        data.testItemValues[getTestId(Test.BIO_AGE, Test.SKELETAL_AGE_SB)]
      ),
      unit: 'yearShort'
    },
    chronoVsBio: {
      value: getValue(
        data.testItemValues[
          getTestId(
            Test.CHRONO_VS_BIO_AGE,
            Test.AGE_AT_TEST_DATE_VS_SKELETAL_AGE
          )
        ]
      ),
      unit: 'yearLong'
    }
  };

  if (method === GrowthTrackerEstimationMethods.KHAMIS_ROCHE) {
    rowData.pah90Ci = {
      value: getValue(
        data.testItemValues[Test.ADULT_HEIGHT_PREDICTION_CI] //'f7529cf9-ba05-4570-bedd-6213ee3b6ec8'
      ),
      unit: 'cm'
    };
  }

  return rowData;
};

const GrowthPanelContextProvider = ({ groupId, children }) => {
  const intl = useIntl();
  const location = useHistory();

  const growthData = useRef(null);
  const refetchCount = useRef(0);

  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [sorting, setSorting] = useState([
    {
      id: 'person',
      desc: false
    }
  ]);
  const [columnOrder, setColumnOrder] = useState([
    'person',
    'heightVelocity', // growth velocity
    'predictedAdultHeight', // % PAH
    'weightVelocity', // weight velocity
    'preciseAge', // chrono age
    'bioAge', // bio age
    'chronoVsBio', // chrono vs bio
    'developmentZScore', // maturation timing
    'growthPhase', // maturation status
    'bodyHeight', // body height
    'adultHeightPrediction', // AH prediction
    'bodyWeight', // body weight
    'pah90Ci' // pah 90%
  ]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sidePanelData, setSidePanelData] = useState(null);
  const [error, setError] = useState(null);
  const [method, setMethod] = useState(
    GrowthTrackerEstimationMethods.KHAMIS_ROCHE
  );
  const [showColumnVisibilityModal, setShowColumnVisibilityModal] =
    useState(false);

  const [
    fetchGrowthDataByGroup,
    { data, loading, error: fetchError, refetch }
  ] = useLazyQuery(QUERY_GET_GROWTH_DATA_OF_GROUP);

  const [getTestDataHistory] = useLazyQuery(QUERY_GET_TESTDATA_HISTORY);

  useEffect(() => {
    const fetchData = async () => {
      await fetchGrowthDataByGroup({
        variables: {
          entityId: groupId
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true
      });
    };

    if (groupId) {
      fetchData().catch(error => {
        Sentry.captureException(error);
      });
    }
  }, [groupId, location]);

  useEffect(() => {
    if (data?.getGrowthDataOfGroup2) {
      growthData.current = data.getGrowthDataOfGroup2;

      setRows(
        growthData.current.map(d => {
          return {
            id: d.person.id,
            person: d.person,
            alertOldData: getAlertOldData(d),
            ...getRowData(d, method)
          };
        })
      );
      setCols(GrowthTrackerColumns(intl, method));
    }
  }, [data]);

  useEffect(() => {
    if (fetchError) {
      if (
        fetchError.graphQLErrors.find(
          e => e.errorType === 'Lambda:ExecutionTimeoutException'
        )
      ) {
        growthData.current = null;
        setRows([]);
        if (refetchCount.current >= 5) {
          setError('TimeoutError');
          return;
        }
        setError('Timeout');
        refetchCount.current += 1;
        refetch();
      } else {
        growthData.current = null;
        setRows([]);

        setError(fetchError);
      }
    }
  }, [fetchError]);

  const onChangeMethod = method => {
    setMethod(method);

    setRows(
      growthData.current.map(d => {
        return {
          id: d.person.id,
          person: d.person,
          alertOldData: getAlertOldData(d),
          ...getRowData(d, method)
        };
      })
    );
    setCols(GrowthTrackerColumns(intl, method));
  };

  const loadTestDataHistory = async ({ person, testItemId, unit }) => {
    const result = await getTestDataHistory({
      variables: {
        personId: person.id,
        testItemId: testItemId
      }
    });

    if (result.data?.getTestDataHistory) {
      if (result.data.getTestDataHistory.length === 0) {
        return null;
      }
      return result.data.getTestDataHistory.map(d => {
        let values = d.values;
        if (typeof values === 'string') {
          try {
            values = JSON.parse(values);
          } catch (e) {
            values = null;
          }
        }
        const value =
          values && Array.isArray(values) && values.length > 0
            ? values[0] !== null && values[0] !== undefined
              ? Number(parseFloat(values[0]).toPrecision(4))
              : values[0]
            : null;
        const dateValue = d?.testDateTime
          ? format(new Date(d.testDateTime), 'yyyy-LL-dd')
          : 0;
        const dateString = d?.testDateTime
          ? format(new Date(d.testDateTime), 'dd/LL/yyyy')
          : '';
        return {
          x: dateValue,
          y: value,
          result: value,
          date: dateString,
          sortDate: dateValue,
          unit: unit,
          inputType: 'device'
        };
      });
    }

    return null;
  };

  const onClickCell = async (cell, testItemId, unitValue) => {
    const person = cell.row.original.person;

    const dataObj = {
      dataSrc: loadTestDataHistory,
      dataSrcProps: { person, testItemId, unit: unitValue },
      title: `${person.firstname} ${person.lastname}`,
      subTitle: idToNameTestItem[testItemId],
      testItemId,
      person,
      unit: unitValue,
      laterality: '2',
      chartData: {
        min: 0,
        max: 10,
        avg: null,
        benchmarkType: 'default',
        data: null
      }
    };

    setSidePanelData(dataObj);
  };

  const onCloseSidePanel = () => setSidePanelData(null);

  return (
    <GrowthPanelContext.Provider
      value={{
        loading,
        cols,
        rows,
        columnVisibility,
        setColumnVisibility,
        sorting,
        setSorting,
        columnOrder,
        onClickCell,
        sidePanelData,
        onCloseSidePanel,
        error,
        method,
        onChangeMethod,
        showColumnVisibilityModal,
        setShowColumnVisibilityModal
      }}
    >
      {children}
    </GrowthPanelContext.Provider>
  );
};

function useGrowthPanelContext() {
  const context = useContext(GrowthPanelContext);
  if (context === undefined) {
    throw new Error(
      'The GrowthPanelContext hook must be used within a GrowthPanelContext.Provider'
    );
  }
  return context;
}

export { GrowthPanelContextProvider, useGrowthPanelContext };
